import React from 'react';
import { useTranslation } from 'react-i18next';
import { Cloud, Laptop, MessageCircle, Moon, ScanLine, Send, WifiOff } from 'lucide-react';
import { namespace } from '@common-config/constants';
import { isClearFlow } from '@common/utils/clear-flow';
import type { ConnectionKind } from '@common-reducers/ConnectedEnginesSlice';
import { C, ConnectCard, OptionRow, PrimaryButton } from '@/pages/onboarding/onboarding-ui';

/**
 * The top of /connections once the user is set up (the connect wizard — ConnectFlow — owns the
 * card until then): ONE clear next move for where the user is.
 *
 *   attention → a gateway waiting on the user (scan / reconnect) comes first
 *   local     → "connected from this computer"; send a test until done; the always-on option
 *   remote    → "always on" — nothing to do
 *   none      → the clear (white-label) flow only — it has no wizard
 *
 * Presentation only: the page reads state and passes callbacks into its existing flows
 * (create gateway, pairing dialog, seeded composer).
 */
export interface ConnectionGuideProps {
  kind: ConnectionKind;
  /** A gateway that needs the user: 'scan' = waiting on a QR / code, 'offline' = dropped. */
  attention?: { engineId: string; reason: 'scan' | 'offline' };
  /** The user already sent their first test — the moment to offer always-on. */
  selfTestDone: boolean;
  isCreating: boolean;
  onGoAlwaysOn: () => void;
  onFixGateway: (engineId: string) => void;
  onSendTest: () => void;
  onTrack: (cta: string) => void;
}

const ATTENTION_FG = 'var(--connect-attention-fg, #92400e)';
const ATTENTION_SOLID = '#b45309'; // amber-700: carries a white icon in light and dark

/** A soft tinted circle with a coloured icon — reads the state at a glance without shouting. */
const IconBadge: React.FC<{ children: React.ReactNode; tone?: 'brand' | 'success' | 'attention' }> = ({ children, tone = 'brand' }) => {
  const color = tone === 'success' ? C.success : tone === 'attention' ? ATTENTION_SOLID : C.ink;
  return (
    <span aria-hidden className="onb-pop" style={{ width: 44, height: 44, borderRadius: 999, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `color-mix(in srgb, ${color} 12%, transparent)`, color }}>
      {children}
    </span>
  );
};

const Title: React.FC<{ title: string; body: string; color?: string }> = ({ title, body, color }) => (
  <div style={{ minWidth: 0 }}>
    <h5 style={{ margin: 0, fontSize: 19, fontWeight: 650, color: color ?? C.text, letterSpacing: '-.01em', lineHeight: 1.3 }}>{title}</h5>
    <p style={{ margin: '4px 0 0', fontSize: 14, color: C.muted, lineHeight: 1.55, maxWidth: 680 }}>{body}</p>
  </div>
);

/** Icon · title/body · the one button — the shape every state below shares. */
const Row: React.FC<{ icon: React.ReactNode; title: string; body: string; color?: string; action?: React.ReactNode }> = ({ icon, title, body, color, action }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 20 }}>
    {icon}
    <div style={{ flex: '1 1 320px', minWidth: 0 }}><Title title={title} body={body} color={color} /></div>
    {action}
  </div>
);

export const ConnectionGuide: React.FC<ConnectionGuideProps> = ({
  kind, attention, selfTestDone, isCreating, onGoAlwaysOn, onFixGateway, onSendTest, onTrack,
}) => {
  const { t, i18n } = useTranslation(namespace);
  const g = (key: string, opts?: Record<string, unknown>) => t(`enginesManagement.guide.${key}`, opts);
  const clearFlow = isClearFlow();
  const dir = i18n.dir(i18n.resolvedLanguage || i18n.language) === 'rtl' ? 'rtl' : 'ltr';

  const goAlwaysOn = () => { onTrack('remote_connection'); onGoAlwaysOn(); };
  const sendTest = () => { onTrack('send_test'); onSendTest(); };
  const alwaysOnRow = !clearFlow && (
    <div style={{ marginTop: 24 }}>
      <OptionRow icon={<Moon size={18} />} onClick={goAlwaysOn} busy={isCreating} ctaButton title={g('alwaysOnTitle')} badge={g('pro')} body={g('alwaysOnBody')} cta={g('alwaysOnCta')} />
    </div>
  );
  const sendTestButton = !selfTestDone && <PrimaryButton icon={<Send size={16} />} onClick={sendTest}>{g('sendTestCta')}</PrimaryButton>;

  let content: React.ReactNode;
  if (attention) {
    const scan = attention.reason === 'scan';
    content = (
      <ConnectCard dir={dir}>
        <Row
          icon={<IconBadge tone="attention">{scan ? <ScanLine size={26} /> : <WifiOff size={26} />}</IconBadge>}
          title={g(scan ? 'attentionTitle' : 'offlineTitle')}
          body={g(scan ? 'attentionBody' : 'offlineBody')}
          color={ATTENTION_FG}
          action={<PrimaryButton onClick={() => { onTrack('fix_gateway'); onFixGateway(attention.engineId); }}>{g(scan ? 'attentionCta' : 'offlineCta')}</PrimaryButton>}
        />
      </ConnectCard>
    );
  } else if (kind === 'none') {
    // Clear (white-label) flow only — the regular product renders the ConnectFlow wizard instead.
    content = (
      <ConnectCard dir={dir}>
        <Row
          icon={<IconBadge><MessageCircle size={26} /></IconBadge>}
          title={g('startTitle')}
          body={g('startBody')}
          action={<PrimaryButton busy={isCreating} onClick={goAlwaysOn}>{g('alwaysOnCta')}</PrimaryButton>}
        />
      </ConnectCard>
    );
  } else if (kind === 'remote') {
    content = (
      <ConnectCard dir={dir}>
        <Row icon={<IconBadge tone="success"><Cloud size={26} /></IconBadge>} title={g('remoteTitle')} body={g(selfTestDone ? 'remoteBody' : 'remoteBodyNext')} action={sendTestButton} />
      </ConnectCard>
    );
  } else {
    // Local: straight to the first message; always-on is the one thing a browser connection can't do.
    content = (
      <ConnectCard dir={dir}>
        <Row icon={<IconBadge tone="success"><Laptop size={26} /></IconBadge>} title={g('localTitle')} body={g(selfTestDone ? 'localBody' : 'localBodyNext')} action={sendTestButton} />
        {alwaysOnRow}
      </ConnectCard>
    );
  }

  return <>{content}</>;
};
